import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  defaultId?: string;
  className?: string;
  /** Pill style with animated indicator */
  variant?: 'pill' | 'underline';
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultId,
  className,
  variant = 'pill',
}) => {
  const [activeId, setActiveId] = useState(defaultId ?? tabs[0]?.id ?? '');
  const indicatorRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0];

  useEffect(() => {
    const activeEl = navRef.current?.querySelector('[data-active="true"]');
    const indicator = indicatorRef.current;
    if (!activeEl || !indicator) return;
    const parent = navRef.current;
    if (!parent) return;
    const left = (activeEl as HTMLElement).offsetLeft;
    const width = (activeEl as HTMLElement).offsetWidth;
    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
  }, [activeId, tabs]);

  return (
    <div className={clsx('w-full max-w-2xl mx-auto', className)}>
      <div
        ref={navRef}
        className={clsx(
          'relative flex rounded-lg p-1',
          variant === 'pill' && 'bg-app-surface-muted/80 dark:bg-gray-800/80',
          variant === 'underline' && 'border-b border-app-border dark:border-gray-700'
        )}
        role="tablist"
      >
        {variant === 'pill' && (
          <div
            ref={indicatorRef}
            className="absolute top-1 bottom-1 rounded-md bg-app-surface dark:bg-gray-700 shadow-sm transition-all duration-300 ease-out"
            style={{ left: 4 }}
            aria-hidden
          />
        )}
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeId === t.id}
            aria-controls={`tabpanel-${t.id}`}
            id={`tab-${t.id}`}
            data-active={activeId === t.id}
            onClick={() => setActiveId(t.id)}
            className={clsx(
              'relative z-10 flex-1 px-4 py-3 text-sm font-medium rounded-md transition-colors',
              activeId === t.id
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-white',
              variant === 'underline' &&
                activeId === t.id &&
                'border-b-2 border-primary-500 -mb-px'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div
        id={activeTab ? `tabpanel-${activeTab.id}` : undefined}
        role="tabpanel"
        aria-labelledby={activeTab ? `tab-${activeTab.id}` : undefined}
        className="mt-6 p-6 rounded-xl bg-app-surface dark:bg-gray-800/80 border border-app-border dark:border-gray-700 min-h-[140px] transition-opacity duration-300"
      >
        {activeTab?.content}
      </div>
    </div>
  );
};
